import { FC, useMemo } from "react";
import { useSelector } from "react-redux";

import { IPickerListItem, Picker } from "@livechat/design-system-react-components";

import { Filter, NotTaggedFilterId } from "constants/filters";
import { mapTagsToOptionsWithGrouping } from "helpers/tags";
import { ITag } from "store/entities/tags";
import { ITeam, selectTeamsAlphabetically } from "store/entities/teams";

import { createRemovedTagOption } from "../helpers";
import { IFilterConfig, IFilterUpdate } from "../interfaces";
import { withFilterBase } from "../withFilterBase";

interface IProps {
  filterValues: string[];
  tags: ITag[];
  filterConfig: IFilterConfig;
  onChange(filterUpdate: IFilterUpdate<string[] | null>): void;
  multiple?: boolean;
}

function getItems(tags: ITag[], selected: string[] | null, teams: ITeam[]): IPickerListItem[] {
  const noTagsSelected = (selected || []).includes(NotTaggedFilterId);

  return mapTagsToOptionsWithGrouping(tags, teams).map(option => ({
    ...option,
    disabled: noTagsSelected && option.key !== NotTaggedFilterId,
  }));
}

const TagFilter: FC<IProps> = props => {
  const { tags, onChange, multiple, filterValues } = props;
  const teams = useSelector(selectTeamsAlphabetically);

  const handleChange = (selectedItems: IPickerListItem[] | null): void => {
    const value = selectedItems?.map(item => item.key) ?? [];
    onChange({
      name: Filter.Tag,
      value: value.includes(NotTaggedFilterId)
        ? [NotTaggedFilterId]
        : value.length
          ? value.filter(tag => tag !== NotTaggedFilterId)
          : null,
    });
  };

  const items = useMemo(() => getItems(tags, filterValues, teams), [tags, filterValues, teams]);

  const selectedTags = useMemo(
    () =>
      filterValues?.map(tagId => {
        const tag = tags.find(tag => tag.id === tagId);

        return tag
          ? {
              key: tag.id,
              name: tag.name,
            }
          : createRemovedTagOption(tagId);
      }),
    [tags, filterValues]
  );

  return (
    <Picker
      id="tag-filter"
      placeholder="Search…"
      type={multiple ? "multi" : "single"}
      openedOnInit
      options={items}
      selected={selectedTags}
      hideClearButton
      onSelect={handleChange}
      clearSearchAfterSelection
    />
  );
};

export default withFilterBase(TagFilter);
